// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling.WEB.3 / whatsapp-calling-ownership.UI.8 — the call as a
// timeline: who was rung (by tier, when the payload says so), who answered on
// which device, every handover (from → to, Talk first / Pass now, the note),
// hold periods when the payload carries them, and how it ended. Pure: it reads
// the normalised call (kaptionCallModel.normalizeCall) and returns rows the
// detail pane and the bubble render. Fields the backend does not send yet
// (ring tiers, holds, pass mode on a leg) simply produce no row.
import { CALL_STATUS } from './kaptionCallModel';

export const TIMELINE_KIND = Object.freeze({
  RINGING: 'ringing',
  DIALED: 'dialed',
  PERMISSION: 'permission',
  ANSWERED: 'answered',
  PASSED: 'passed',
  HOLD: 'hold',
  LIVE: 'live',
  ENDED: 'ended',
  MISSED: 'missed',
  DECLINED: 'declined',
  FAILED: 'failed',
});

const parse = iso => {
  const at = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(at) ? null : at;
};

// "2:10", "1:02:05" — the compact clock the transcript and the bubble use.
export const formatClock = totalSeconds => {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = n => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

export const firstName = name => (name || '').trim().split(/\s+/)[0] || '';

// How long one person was on the call: the leg's own figure, else joined →
// left (or → now while the leg is open).
export const legSeconds = (leg, now = Date.now()) => {
  if (leg?.durationSeconds !== null && leg?.durationSeconds !== undefined)
    return Number(leg.durationSeconds) || 0;
  const start = parse(leg?.joinedAt);
  if (start === null) return null;
  const end = parse(leg.leftAt) ?? now;
  return Math.max(0, (end - start) / 1000);
};

// "Ana 2:10 → Bruno 4:32" as data: one entry per leg.
export const legChain = (legs = [], now = Date.now()) =>
  (legs || []).map((leg, index) => {
    const seconds = legSeconds(leg, now);
    return {
      key: leg.id || index,
      name: leg.user?.name || '',
      shortName: firstName(leg.user?.name),
      passedBy: index === 0 ? null : leg.passedBy || legs[index - 1]?.user,
      duration: seconds === null ? '' : formatClock(seconds),
      note: leg.note || null,
      live: !leg.leftAt && leg.durationSeconds == null,
      // whatsapp-calling-ai.UI.2 — the AI's leg gets the AI chip.
      assistant: leg.kind === 'assistant',
    };
  });

// END_REASONS i18n suffix for a backend end_reason; unknown → null (the
// caller shows nothing rather than a raw code).
const END_REASON_KEYS = {
  agent_hangup: 'AGENT_HANGUP',
  customer_hangup: 'CUSTOMER_HANGUP',
  max_duration: 'MAX_DURATION',
  no_media: 'NO_MEDIA',
  media_timeout: 'NO_MEDIA',
  media_idle: 'MEDIA_IDLE',
  ring_timeout: 'RING_TIMEOUT',
  no_answer: 'NO_ANSWER',
  agent_disconnected: 'AGENT_DISCONNECTED',
  terminated_before_connect: 'TERMINATED_BEFORE_CONNECT',
  declined: 'DECLINED',
};
export const endReasonKey = reason =>
  reason && END_REASON_KEYS[reason]
    ? `KAPTION_CALLS.END_REASONS.${END_REASON_KEYS[reason]}`
    : null;

const DEVICE_KEYS = { web: 'WEB', mobile: 'MOBILE', desktop: 'DESKTOP' };
export const deviceKey = device =>
  device && DEVICE_KEYS[device]
    ? `KAPTION_CALLS.DEVICES.${DEVICE_KEYS[device]}`
    : null;

const callStart = call => {
  const candidates = [
    call.ringStartedAt,
    call.startedAt,
    call.legs?.[0]?.joinedAt,
    call.answeredAt,
  ];
  return candidates.map(parse).find(value => value !== null) ?? null;
};

export const buildCallTimeline = (call, { now = Date.now() } = {}) => {
  if (!call) return [];
  const start = callStart(call);
  const rows = [];
  const push = (row, iso) => {
    const at = parse(iso);
    rows.push({
      ...row,
      at: iso || null,
      offsetSeconds:
        at !== null && start !== null ? Math.max(0, (at - start) / 1000) : null,
    });
  };
  const isOutbound = call.direction === 'outbound';
  const legs = call.legs || [];

  // Ringing — one row per tier when the payload names them.
  if (call.status === CALL_STATUS.PERMISSION_REQUESTED) {
    push(
      { key: 'permission', kind: TIMELINE_KIND.PERMISSION },
      call.ringStartedAt || call.startedAt
    );
  } else if (isOutbound) {
    push(
      {
        key: 'dialed',
        kind: TIMELINE_KIND.DIALED,
        user: legs[0]?.user || call.answeredBy || null,
      },
      call.ringStartedAt || call.startedAt
    );
  } else if (call.ringTiers?.length) {
    call.ringTiers.forEach((tier, index) =>
      push(
        {
          key: `ring-${index}`,
          kind: TIMELINE_KIND.RINGING,
          tier: tier.tier ?? index + 1,
          users: tier.users || [],
        },
        tier.startedAt || (index === 0 ? call.ringStartedAt : null)
      )
    );
  } else {
    push(
      { key: 'ring', kind: TIMELINE_KIND.RINGING, users: [] },
      call.ringStartedAt || call.startedAt
    );
  }

  // Answered + every handover.
  legs.forEach((leg, index) => {
    const seconds = legSeconds(leg, now);
    if (index === 0) {
      push(
        {
          key: `leg-${leg.id || index}`,
          kind: TIMELINE_KIND.ANSWERED,
          outbound: isOutbound,
          user: leg.user,
          device: leg.device,
          onItSeconds: seconds,
          live: !leg.leftAt && leg.durationSeconds == null,
          // whatsapp-calling-ai.UI.2 — the AI answered.
          assistant: leg.kind === 'assistant',
        },
        leg.joinedAt
      );
      return;
    }
    push(
      {
        key: `leg-${leg.id || index}`,
        kind: TIMELINE_KIND.PASSED,
        fromUser: leg.passedBy || legs[index - 1]?.user || null,
        user: leg.user,
        device: leg.device,
        note: leg.note,
        passMode: leg.passMode || null,
        // whatsapp-calling-ai.UI.2 — "<assistant> passed to <agent>".
        assistant: leg.kind === 'assistant',
        fromAssistant:
          leg.passedBy?.kind === 'assistant' ||
          legs[index - 1]?.kind === 'assistant',
        onItSeconds: seconds,
        live: !leg.leftAt && leg.durationSeconds == null,
      },
      leg.joinedAt
    );
  });
  if (!legs.length && call.answeredBy?.name && call.answeredAt) {
    push(
      {
        key: 'answered',
        kind: TIMELINE_KIND.ANSWERED,
        outbound: isOutbound,
        user: call.answeredBy,
        device: null,
        onItSeconds: call.durationSeconds,
      },
      call.answeredAt
    );
  }

  (call.holds || []).forEach((hold, index) => {
    const from = parse(hold.startedAt);
    const to = parse(hold.endedAt) ?? (from !== null ? now : null);
    push(
      {
        key: `hold-${index}`,
        kind: TIMELINE_KIND.HOLD,
        user: hold.user || null,
        durationSeconds:
          from !== null && to !== null ? Math.max(0, (to - from) / 1000) : null,
        live: !hold.endedAt,
      },
      hold.startedAt
    );
  });

  // How it ended (or that it is still live).
  const ENDINGS = {
    [CALL_STATUS.COMPLETED]: TIMELINE_KIND.ENDED,
    [CALL_STATUS.MISSED]: TIMELINE_KIND.MISSED,
    [CALL_STATUS.DECLINED]: TIMELINE_KIND.DECLINED,
    [CALL_STATUS.FAILED]: TIMELINE_KIND.FAILED,
  };
  if (ENDINGS[call.status]) {
    push(
      {
        key: 'end',
        kind: ENDINGS[call.status],
        outbound: isOutbound,
        reasonKey: endReasonKey(call.endReason),
        durationSeconds: call.durationSeconds,
      },
      call.endedAt
    );
  } else if (call.status === CALL_STATUS.ANSWERED) {
    push({ key: 'live', kind: TIMELINE_KIND.LIVE }, null);
  }

  // Chronological. A row with no time sorts as if it happened with the row
  // before it, so it keeps its place in the story.
  let carried = -Infinity;
  const order = rows.map((row, index) => {
    const at = parse(row.at);
    if (at !== null) carried = at;
    return { row, index, t: at ?? carried };
  });
  order.sort((a, b) => a.t - b.t || a.index - b.index);
  return order.map(({ row }) => row);
};
